<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { MultiSelect } from 'primevue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useProductStore } from 'shared/products/stores/productStore';
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import DateRangeFilter from '../../shared/DateRangeFilter.vue';
import { ProductType } from 'shared/products/domain/productType';

interface DateRangeValues {
  from: string;
  to: string;
}

interface DateRangeFilterExposed {
  getFilterValues: () => DateRangeValues;
}

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const dateRangeFilterRef = ref<DateRangeFilterExposed | null>(null);
const productIds = ref<string[]>([]);

const productStore = useProductStore();
const refreshProductStore = useRefereshProductStore();

const products = computed(() => {
  return [...productStore.sortedByName]
    .filter((product) => product.type === ProductType.Log)
    .sort((a, b) =>
      a.name.localeCompare(b.name, languageStore.language.code, {
        numeric: true,
        sensitivity: 'base',
      }),
    )
    .map((product) => ({
      id: product.id,
      name: product.name,
    }));
});

onMounted(async () => {
  await refreshProductStore.initialize();
});

defineExpose({
  getFilterValues() {
    const dateRange = dateRangeFilterRef.value?.getFilterValues() ?? { from: '', to: '' };
    return {
      from: dateRange.from,
      to: dateRange.to,
      productIds: productIds.value,
    };
  },
});
</script>

<template>
  <div class="d-flex align-items-center gap-2 flex-nowrap">
    <DateRangeFilter ref="dateRangeFilterRef"></DateRangeFilter>

    <div class="inline-block">
      <span class="ms-3 me-1">{{ translations.generic.products }}</span>
      <MultiSelect id="product-select" v-model="productIds" :options="products" optionLabel="name"
        optionValue="id" :placeholder="translations.generic.products" display="chip" filter class="w-15rem"
        :maxSelectedLabels="1"></MultiSelect>
    </div>
  </div>
</template>
